import { useState, useCallback, useMemo } from 'react'
import { getMarkerFilterCategory } from '../pages/map/utils'

/**
 * Custom hook for managing map filters with toggle functionality
 * Combines filtering logic with toggle handler for better code reuse
 * 
 * @param {Array} entities - Array of entities to filter
 * @param {string|null} isolatedEntityId - Entity ID to show exclusively (null to show all)
 * @param {Set<string>} openNowIds - Set of entity IDs that are open right now
 * @param {boolean} openNowActive - Whether the Open Now filter is active
 * @returns {Object} Filtered entities, active filters, and toggle handler
 */
export function useMapFiltersWithToggle(entities, isolatedEntityId = null, openNowIds = new Set(), openNowActive = false) {
  const [activeFilters, setActiveFilters] = useState([]) // Empty array = show all

  // Handle legend filter toggle
  const handleFilterToggle = useCallback((filterKey) => {
    setActiveFilters(prev => {
      if (prev.includes(filterKey)) {
        // Remove filter
        return prev.filter(f => f !== filterKey)
      } else {
        // Add filter
        return [...prev, filterKey]
      }
    })
  }, [])

  // Reset all filters
  const resetFilters = useCallback(() => {
    setActiveFilters([])
  }, [])

  // Filter entities based on active filters and isolation
  const filteredEntities = useMemo(() => {
    let filtered = entities

    // Apply isolation first (if any entity is isolated, show only that)
    if (isolatedEntityId !== null) {
      filtered = filtered.filter(e => e.entityId === isolatedEntityId)
    }

    // Apply Open Now filter
    if (openNowActive && openNowIds.size > 0) {
      filtered = filtered.filter(e => openNowIds.has(e.entityId || e.id))
    }

    // If no category filters active, show all (remaining after isolation/openNow)
    if (activeFilters.length === 0) {
      return filtered
    }

    // Otherwise, filter by active categories
    return filtered.filter(entity => {
      const category = getMarkerFilterCategory(entity.confirmationCount || 0)
      return activeFilters.includes(category)
    })
  }, [entities, activeFilters, isolatedEntityId, openNowIds, openNowActive])

  return {
    filteredEntities,
    activeFilters,
    handleFilterToggle,
    resetFilters
  }
}
